// Keeping the line registered while the tab is hidden, and after the laptop
// sleeps. Pure decisions (the Phone engine does the work), unit-tested.
//
// Why: Chrome slows timers in a tab hidden for more than ~5 minutes to about
// once a minute. JsSIP re-registers somewhere between 50 % and 89 % of the
// 300 s registration, so a timer that fires up to a minute late could let the
// registration lapse and calls would not ring. We refresh it ourselves every
// 2 minutes (worst case ~3 minutes with throttling, well inside 300 s), and
// treat a long gap between our own ticks as "the computer was asleep": the
// WebSocket is probably dead even if the browser hasn't noticed yet, so we
// connect again instead of waiting for a timeout.

import type { LineState } from './line-status';

/** How often the engine checks (timers in hidden tabs may run once a minute). */
export const HEARTBEAT_MS = 20_000;
/** A gap this long between ticks means the computer slept (longer than any throttling). */
export const SLEEP_GAP_MS = 150_000;
/** Refresh the registration this often. */
export const REFRESH_EVERY_MS = 120_000;
/** Don't reconnect more often than this. */
export const MIN_RECONNECT_GAP_MS = 10_000;

/** What woke us: the clock jumped, the network came back, the tab became visible, Chrome unfroze the tab. */
export type Wake = 'sleep' | 'online' | 'visible' | 'resume';

/**
 * - reconnect: a fresh connection + registration (only when no call is up)
 * - register: send a REGISTER on the current connection (cheap check that it still works)
 * - restart: load the settings again (they failed to load before)
 */
export type WakeAction = 'reconnect' | 'register' | 'restart' | 'none';

export function wakeAction(why: Wake, line: LineState, inCall: boolean, sinceRefreshMs: number): WakeAction {
  if (line === 'other_tab' || line === 'no_phone' || line === 'starting') return 'none';
  if (line === 'error') return 'restart';
  if (inCall) {
    // never cut a call: only check the registration
    return why === 'visible' && sinceRefreshMs < REFRESH_EVERY_MS / 2 ? 'none' : 'register';
  }
  if (why === 'visible') {
    if (line === 'ready') return sinceRefreshMs >= REFRESH_EVERY_MS / 2 ? 'register' : 'none';
    if (line === 'failed') return 'register';
    return 'reconnect'; // offline / connecting / updating
  }
  // slept, network changed or tab unfrozen: the old connection can't be trusted
  return 'reconnect';
}

/** On each heartbeat: did the computer sleep, or is a registration refresh due? */
export function tickAction(gapMs: number, line: LineState, sinceRefreshMs: number): 'wake' | 'register' | 'none' {
  if (gapMs >= SLEEP_GAP_MS) return 'wake';
  if (line === 'ready' && sinceRefreshMs >= REFRESH_EVERY_MS) return 'register';
  return 'none';
}
